import { apiClient } from './client';

/**
 * 업무자동화 — 목록 · 만들기 · 고치기 · 삭제 · 회차 이력 (설계 판 X1).
 *
 * ★★★ **「다음 실행」을 화면이 세지 않는다.** 서버가 회사 시간대로 세어 `nextRunAt` 으로
 *    실어 준다 — 화면이 시간대를 다시 풀면 둘이 어긋나고, 그때 「화면은 9시라는데 10시에 온다」가 된다.
 * ★★ **한도도 서버가 준다**(`limit`) — 화면에 숫자를 또 적으면 언젠가 서로 달라진다.
 * ★ **시간대가 없으면 `timezone: null`** — 화면은 그때 만들기를 막고 «왜»를 적는다.
 */

/** 사람이 고르는 것은 «창»이다 — 정각을 안 묻는 이유는 몰림(설계 §06) 때문이다. */
export type AutomationWindow = 'MORNING' | 'AFTERNOON' | 'EVENING';
export type AutomationStatus = 'ACTIVE' | 'PAUSED';
/** 때가 되면(SCHEDULE) · 무슨 일이 생기면(EVENT — 판 X2). */
export type AutomationTriggerKind = 'SCHEDULE' | 'EVENT';
/** 지금 있는 사건은 하나다. */
export type AutomationEventKind = 'mail.received';

/**
 * 「어떤 메일이 깨우나」 — 적은 것끼리 **모두 AND** 다.
 *
 * ★ 아무것도 안 적으면 **내 새 메일 전부**가 깨운다. 빈 조건을 「아무것도 안 걸림」으로 읽으면
 *   만들어 놓고 영영 안 도는 자동화가 된다.
 */
export interface MailEventFilter {
  /**
   * **어느 사서함으로 온 것만** — 연동해 둔 외부 메일 계정 id.
   * ★★ `from`(보낸 주소)과 **뜻이 반대**다. 「naver «로» 들어온 것」은 이쪽이다.
   */
  accountIds?: string[];
  /** 소문자 주소. `@naver.com` 처럼 `@` 로 시작하면 그 도메인 전체다. */
  from?: string[];
  /** `true` 일 때만 조건이다 — 「첨부 없는 것만」은 두지 않았다. */
  hasAttachment?: boolean;
  subjectContains?: string;
}

export interface AiAutomation {
  id: string;
  name: string;
  triggerKind: AutomationTriggerKind;
  /** 사건 자동화일 때만. */
  eventKind: AutomationEventKind | null;
  /** 사건 자동화일 때만 — 서버가 **모양을 다시 본 것**을 준다. */
  eventFilter: MailEventFilter | null;
  /** 0=일 … 6=토 (회사 시간대 기준). **사건 자동화에는 없다(null).** */
  weekday: number | null;
  window: AutomationWindow | null;
  /** '오전' 같은 표시용 — 서버와 화면이 같은 말을 쓴다. */
  windowLabel: string | null;
  prompt: string;
  agentId: string | null;
  /** 붙은 에이전트 이름 — 서버가 함께 준다(화면이 id 로 다시 찾으면 둘이 어긋난다). */
  agentName: string | null;
  /**
   * 붙은 에이전트를 **지금 쓸 수 있나**. 안 붙었으면 null.
   * ★ false = 중지된 것이 붙어 있다 → 워커는 **역할 없이** 돈다. 화면이 그 사실을 적어야
   *   「에이전트를 붙였는데 왜 그냥 답하지」가 안 생긴다.
   */
  agentUsable: boolean | null;
  /** 결과가 쌓이는 전용 대화. 첫 회차 전에는 null 이다. */
  conversationId: string | null;
  status: AutomationStatus;
  /** 연속 실패 — 5가 되면 서버가 스스로 멈춘다. */
  failStreak: number;
  pausedReason: string | null;
  /**
   * ★★★ **회사 관리자가 잠갔나.** 잠기면 만든 사람은 다시 켤 수 없다(2026-09-11 사용자 결정).
   * ★ 판정은 서버가 `paused_at` 칸으로 한다 — 화면이 사유 문자열을 보고 판정하면 문구가 바뀔 때
   *   조용히 어긋난다(#1147 에서 실제로 그랬다).
   */
  adminHeld: boolean;
  /** 누가 껐나. **모를 수 있다** — migration 190 이전에 잠긴 것은 기록이 없다. */
  pausedByName: string | null;
  pausedAt: string | null;
  lastRunAt: string | null;
  /** 서버가 회사 시간대로 센 값. 멈춘 것과 **사건 자동화**는 null 이다(메일이 언제 올지는 모른다). */
  nextRunAt: string | null;
}

export interface AiAutomationListResult {
  automations: AiAutomation[];
  limit: number;
  timezone: string | null;
  /** 사건 자동화의 묶음 창(분) — **서버가 준다.** 화면이 「10분」을 따로 적으면 언젠가 어긋난다. */
  eventBucketMinutes: number;
  /** 자동화 하나가 하루에 돌 수 있는 회차 수. */
  eventRunsPerDayMax: number;
  eventKinds: AutomationEventKind[];
}

export interface AiAutomationDraft {
  name: string;
  prompt: string;
  triggerKind?: AutomationTriggerKind;
  /** 시각 자동화일 때만 보낸다. */
  weekday?: number;
  window?: AutomationWindow;
  /** 사건 자동화일 때만 보낸다. */
  eventKind?: AutomationEventKind;
  eventFilter?: MailEventFilter;
  /** 만들 때는 «안 보내면» 안 붙는다. 고칠 때는 **null 을 보내야** 떨어진다. */
  agentId?: string | null;
}

export type AiAutomationRunStatus = 'QUEUED' | 'RUNNING' | 'OK' | 'FAILED' | 'SKIPPED';

export interface AiAutomationRun {
  id: string;
  scheduledFor: string;
  status: AiAutomationRunStatus;
  finishedAt: string | null;
  error: string | null;
  messageId: string | null;
  /** 이 회차를 깨운 메일 통 수 — 시각 자동화는 null. */
  eventCount: number | null;
}

export async function listAutomations(): Promise<AiAutomationListResult> {
  const { data } = await apiClient.get<AiAutomationListResult>('/assist/automations');
  return data;
}

export async function createAutomation(draft: AiAutomationDraft): Promise<AiAutomation> {
  const { data } = await apiClient.post<{ automation: AiAutomation }>('/assist/automations', draft);
  return data.automation;
}

export async function patchAutomation(
  id: string,
  patch: Partial<AiAutomationDraft> & { status?: AutomationStatus },
): Promise<AiAutomation> {
  const { data } = await apiClient.patch<{ automation: AiAutomation }>(
    `/assist/automations/${id}`, patch,
  );
  return data.automation;
}

/** ⚠ 되돌릴 수 없다 — 부르는 쪽이 **먼저** 확인을 받는다. */
export async function deleteAutomation(id: string): Promise<void> {
  await apiClient.delete(`/assist/automations/${id}`);
}

/**
 * 회차 한 줄을 **사람 말로** 옮긴다 (2026-09-11).
 *
 * ★★★ **「안 돌았다」와 「건너뛰었다」와 「실패했다」는 다른 말이다.** 표에 상태가 다섯인 것이
 *   그래서인데, 화면이 전부 「—」로 그리면 그 구별이 통째로 사라진다.
 * ★ 사유(`error`)가 있으면 **그것을 그대로 보여 준다** — 서버가 이미 사람 말로 적어 둔다
 *   (「만든 사람의 계정이 비활성입니다」·「하루 24회 한도를 넘어 건너뛰었습니다」…).
 */
export const RUN_STATUS_LABEL: Record<AiAutomationRunStatus, string> = {
  QUEUED: '기다리는 중',
  RUNNING: '도는 중',
  OK: '답이 왔습니다',
  SKIPPED: '건너뜀',
  FAILED: '실패',
};

export async function listAutomationRuns(id: string): Promise<AiAutomationRun[]> {
  const { data } = await apiClient.get<{ runs: AiAutomationRun[] }>(`/assist/automations/${id}/runs`);
  return data.runs;
}

/** 요일 이름 — 서버는 숫자만 주고 표시는 화면이 한다. */
export const WEEKDAY_LABEL = ['일', '월', '화', '수', '목', '금', '토'] as const;

/** 사건 — 지금은 하나뿐이지만 «고르는 것»으로 둔다(다음 사건이 올 자리). */
export const EVENT_OPTIONS: Array<{ value: AutomationEventKind; label: string }> = [
  { value: 'mail.received', label: '새 메일이 왔을 때' },
];

/** 창 — 고르는 자리와 보여 주는 자리가 같은 목록을 쓴다. */
export const WINDOW_OPTIONS: Array<{ value: AutomationWindow; label: string; hint: string }> = [
  { value: 'MORNING', label: '오전', hint: '9시~12시 사이' },
  { value: 'AFTERNOON', label: '오후', hint: '13시~16시 사이' },
  { value: 'EVENING', label: '저녁', hint: '18시~21시 사이' },
];
